import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CardTitle from "@saleor/components/CardTitle";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import SingleSelectField from "@saleor/components/SingleSelectField";
import { StaffErrorFragment } from "@saleor/fragments/types/StaffErrorFragment";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { commonMessages } from "@saleor/intl";
import { getCityChoices, getProvinceChoices } from "@saleor/staff/utils";
import {
  SmopUserTypeCode,
  StaffPromoCreatorEnum
} from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getStaffErrorMessage from "@saleor/utils/errors/staff";
import getAccountErrorMessage from "@saleor/utils/errors/staff";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import { getStaffUserType } from "@saleor/utils/smop";
import Moment from "moment";
import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { getUserInitials, maybe } from "../../../misc";
import { StaffMemberDetails_user } from "../../types/StaffMemberDetails";
import { PROMO_CREATOR_CHOICES } from "../StaffCreateProperties/constants";

const useStyles = makeStyles(
  theme => ({
    avatar: {
      "& svg": {
        fill: "#fff"
      },
      "&:hover $avatarHover": {
        opacity: 1
      },
      alignItems: "center",
      borderRadius: "100%",
      display: "grid",
      height: 120,
      justifyContent: "center",
      overflow: "hidden",
      position: "relative",
      width: 120
    },
    avatarActionText: {
      "&:hover": {
        textDecoration: "underline"
      },
      cursor: "pointer",
      fontSize: 12
    },
    avatarDefault: {
      "& div": {
        color: "#fff",
        fontSize: 35,
        fontWeight: "bold",
        lineHeight: "120px"
      },
      background: theme.palette.primary.main,
      height: 120,
      textAlign: "center",
      width: 120
    },
    avatarHover: {
      background: "#00000080",
      borderRadius: "100%",
      fontSize: 12,
      fontWeight: 500,
      height: 120,
      opacity: 0,
      padding: theme.spacing(2.5, 0),
      position: "absolute",
      textAlign: "center",
      textTransform: "uppercase",
      transition: "opacity 0.5s",
      width: 120
    },
    avatarImage: {
      pointerEvents: "none",
      width: "100%"
    },
    cardSpacer: {
      marginTop: "20px"
    },
    editButton: {
      bottom: 0,
      position: "absolute",
      right: 0
    },
    editRoot: {
      position: "relative"
    },
    fileField: {
      display: "none"
    },
    formControl: {
      width: "100%"
    },
    prop: {
      marginBottom: theme.spacing(2)
    },
    propGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridRowGap: theme.spacing(1),
      gridTemplateColumns: "1fr 1fr",
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "1fr"
      }
    },
    root: {
      display: "grid",
      gridColumnGap: theme.spacing(4),
      gridTemplateColumns: "120px 1fr"
    },
    staffInformationRoot: {
      display: "grid",
      gridColumnGap: theme.spacing(4),
      gridTemplateColumns: "1fr"
    },
    subRoot: {
      gridColumnGap: theme.spacing(4),
      gridTemplateColumns: "120px 1fr"
    }
  }),
  { name: "StaffProperties" }
);

interface StaffPropertiesProps {
  staffMemberUserType: any;
  assignedMallName: string;
  assignedTenantName: string;
  assignedBrandName: string;
  assignedCompanyNameByBrandId: string;
  canEditAvatar: boolean;
  canChangePassword: boolean;
  canChangeOtherStaffPassword: boolean;
  className?: string;
  data: {
    email: string;
    firstName: string;
    lastName: string;
    contactNo: string;
    mallBranch: string;
    promoCreator: StaffPromoCreatorEnum | null;
  };

  localError: { contactNo: string };
  errors: StaffErrorFragment[];
  disabled: boolean;
  isUserSameAsViewer: boolean;
  // isOtherUser: boolean;
  staffMember: StaffMemberDetails_user;
  selectedPermissionGroup: string;
  onChange: (event: React.ChangeEvent<any>) => void;
  onImageDelete: () => void;
  onImageUpload: (file: File) => void;
  onChangePassword: () => void;
  onChangeOtherPw: () => void;
}

const StaffProperties: React.FC<StaffPropertiesProps> = props => {
  const {
    staffMemberUserType,
    assignedTenantName,
    assignedMallName,
    assignedBrandName,
    assignedCompanyNameByBrandId,
    className,
    canChangePassword,
    canChangeOtherStaffPassword,
    data,
    errors,
    localError,
    staffMember,
    selectedPermissionGroup,
    onChange,
    onChangePassword,
    onChangeOtherPw,
    disabled,
    isUserSameAsViewer
    // isOtherUser
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();

  const formFields = ["contactNo", "email", "firstName", "lastName", "id"];

  const formErrors = getFormErrors(formFields, errors || []);

  const [isRunnerRider, setIsRunnerRider] = React.useState(false);
  const [homeAddress, setHomeAddress] = React.useState(null);
  const [selectedProvince, setSelectedProvince] = React.useState("");
  const [provinceChoices, setProvinceChoices] = React.useState([]);
  const [cityChoices, setCityChoices] = React.useState([]);
  const [provinceDisplayValue, setProvinceDisplayValue] = useStateFromProps(
    maybe(() => {
      const provinceObject = provinceChoices.find(
        pc => pc.value === homeAddress?.provinceCode
      );
      return provinceObject?.label;
    })
  );

  useEffect(() => {
    setIsRunnerRider(
      selectedPermissionGroup === "Runner" ||
        selectedPermissionGroup === "Rider"
    );
    setProvinceChoices(getProvinceChoices());
  }, [selectedPermissionGroup]);

  useEffect(() => {
    setHomeAddress(staffMember?.homeAddress);
  }, [staffMember]);

  const onProvinceChange = createSingleAutocompleteSelectHandler(
    onChange,
    selectedValue => {
      setProvinceDisplayValue(selectedValue);

      const provinceObject = provinceChoices.find(
        pc => pc.label === selectedValue
      );
      setSelectedProvince(provinceObject?.value);
    },
    provinceChoices
  );

  useEffect(() => {
    setCityChoices(getCityChoices(homeAddress?.provinceCode));
  }, [homeAddress?.provinceCode]);

  return (
    <div>
      <Card className={className}>
        <CardTitle
          title={
            isUserSameAsViewer
              ? intl.formatMessage({
                  defaultMessage: "User Informations",
                  description: "section header"
                })
              : intl.formatMessage({
                  defaultMessage: "User Information",
                  description: "section header"
                })
          }
        />
        <CardContent>
          <div className={classes.root}>
            <div>
              <div className={classes.avatar}>
                {maybe(() => staffMember.avatar.url) ? (
                  <img
                    className={classes.avatarImage}
                    src={maybe(() => staffMember.avatar.url)}
                  />
                ) : (
                  <div className={classes.avatarDefault}>
                    <Typography>{getUserInitials(data)}</Typography>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className={classes.propGrid}>
                <div>
                  <Typography variant="h3" gutterBottom>
                    {getStaffUserType(staffMember)?.description ||
                      staffMember?.permissionGroups[0]?.name}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
        {!!formErrors.id && (
          <CardContent>
            <Typography color="error">
              {getStaffErrorMessage(formErrors.id, intl)}
            </Typography>
          </CardContent>
        )}
      </Card>

      <div className={classes.cardSpacer}>
        <Card className={className}>
          <CardTitle
            title={intl.formatMessage(commonMessages.generalInformations)}
          />
          <CardContent>
            <div className={classes.staffInformationRoot}>
              <div className={classes.propGrid}>
                <div className={classes.prop}>
                  <TextField
                    id="firstNameTxt"
                    disabled={disabled}
                    error={!!formErrors.firstName}
                    helperText={getAccountErrorMessage(
                      formErrors.firstName,
                      intl
                    )}
                    label={intl.formatMessage(commonMessages.firstName)}
                    value={data.firstName}
                    name="firstName"
                    onChange={onChange}
                    fullWidth
                  />
                </div>
                <div className={classes.prop}>
                  <TextField
                    id="lastNameTxt"
                    disabled={disabled}
                    error={!!formErrors.lastName}
                    helperText={getAccountErrorMessage(
                      formErrors.lastName,
                      intl
                    )}
                    label={intl.formatMessage(commonMessages.lastName)}
                    value={data.lastName}
                    name="lastName"
                    onChange={onChange}
                    fullWidth
                  />
                </div>
                <div className={classes.prop}>
                  <TextField
                    id="emailTxt"
                    disabled={disabled}
                    error={!!formErrors.email}
                    helperText={getAccountErrorMessage(formErrors.email, intl)}
                    label={intl.formatMessage(commonMessages.email)}
                    value={data.email}
                    name="email"
                    fullWidth
                    onChange={onChange}
                  />
                </div>
                <div className={classes.prop}>
                  <TextField
                    id="contactNoTxt"
                    disabled={disabled}
                    error={
                      /* !!formErrors.contactNo || */ !!localError.contactNo
                    }
                    label={intl.formatMessage(commonMessages.contactNumber)}
                    helperText={!!localError.contactNo && localError.contactNo}
                    /* : getAccountErrorMessage(
                      formErrors.contactNo,
                      intl
                    )*/
                    value={data.contactNo}
                    name="contactNo"
                    onChange={onChange}
                    inputProps={{
                      maxLength: 12
                    }}
                    onKeyPress={event => {
                      if (!/[0-9]/.test(event.key)) {
                        event.preventDefault();
                      }
                    }}
                    fullWidth
                  />
                </div>
                {maybe(() => !!data.promoCreator) && (
                  <div className={classes.prop}>
                    <SingleSelectField
                      name="promoCreator"
                      choices={PROMO_CREATOR_CHOICES}
                      disabled={disabled}
                      onChange={onChange}
                      label={intl.formatMessage({
                        defaultMessage: "Promo Creator"
                      })}
                      value={data.promoCreator}
                      lowerDropdown
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
          {!!formErrors.id && (
            <CardContent>
              <Typography color="error">
                {getStaffErrorMessage(formErrors.id, intl)}
              </Typography>
            </CardContent>
          )}
        </Card>
      </div>
      <div className={classes.cardSpacer}>
        <Card className={className}>
          <CardTitle
            title={intl.formatMessage(commonMessages.accountInformations)}
          />
          <CardContent>
            {[
              SmopUserTypeCode.MALL_ADMIN,
              SmopUserTypeCode.OMI_OPS,
              SmopUserTypeCode.RIDER,
              SmopUserTypeCode.RUNNER
            ].includes(staffMemberUserType?.code) ? (
              <div>
                <div className={classes.propGrid}>
                  <div className={classes.prop}>
                    <TextField
                      id="mallTxt"
                      disabled={true}
                      label={intl.formatMessage({
                        defaultMessage: "Mall",
                        description: "input label"
                      })}
                      value={assignedMallName}
                      fullWidth
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {isRunnerRider && (
              <>
                <div>
                  <div className={classes.propGrid}>
                    <div className={classes.prop}>
                      <TextField
                        id="companyNameTxt"
                        label={intl.formatMessage(commonMessages.companyName)}
                        value={homeAddress?.companyName}
                        fullWidth
                        disabled
                      />
                    </div>
                    <div className={classes.prop}></div>
                    <div className={classes.prop}>
                      <TextField
                        id="streetBrgyTxt"
                        label={intl.formatMessage(
                          commonMessages.homeAddressBrgy
                        )}
                        value={homeAddress?.streetBrgy}
                        fullWidth
                        disabled
                      />
                    </div>
                    <div className={classes.prop}>
                      <SingleAutocompleteSelectField
                        displayValue={provinceDisplayValue}
                        label={intl.formatMessage(
                          commonMessages.homeAddressProvince
                        )}
                        value={selectedProvince}
                        choices={provinceChoices}
                        InputProps={{
                          autoComplete: "off"
                        }}
                        name="provinceCode"
                        onChange={onProvinceChange}
                        disabled
                      />
                    </div>
                    <div className={classes.prop}>
                      <SingleAutocompleteSelectField
                        displayValue={homeAddress?.city}
                        label={intl.formatMessage(
                          commonMessages.homeAddressCity
                        )}
                        value={homeAddress?.city}
                        choices={cityChoices}
                        InputProps={{
                          autoComplete: "off"
                        }}
                        name="city"
                        onChange={null}
                        disabled
                      />
                    </div>
                    <div className={classes.prop}>
                      <TextField
                        id="postalCodeTxt"
                        label={intl.formatMessage(
                          commonMessages.homeAddressZipCode
                        )}
                        value={homeAddress?.postalCode}
                        fullWidth
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {staffMemberUserType?.code === SmopUserTypeCode.SELLER_MASTER ? (
              <>
                <div>
                  <div className={classes.propGrid}>
                    <div className={classes.prop}>
                      <TextField
                        id="tradeNameTxt"
                        disabled={true}
                        label={intl.formatMessage({
                          defaultMessage: "Trade Name",
                          description: "input label"
                        })}
                        value={assignedBrandName}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className={classes.propGrid}>
                    <div className={classes.prop}>
                      <TextField
                        id="companyNameTxt"
                        disabled={true}
                        label={intl.formatMessage({
                          defaultMessage: "Company Name",
                          description: "input label"
                        })}
                        value={assignedCompanyNameByBrandId}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            {staffMemberUserType?.code === SmopUserTypeCode.STORE_ADMIN ? (
              <>
                <div>
                  <div className={classes.propGrid}>
                    <div className={classes.prop}>
                      <TextField
                        id="tradeNameTxt"
                        disabled={true}
                        label={intl.formatMessage({
                          defaultMessage: "Trade Name",
                          description: "input label"
                        })}
                        value={assignedBrandName}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className={classes.propGrid}>
                    <div className={classes.prop}>
                      <TextField
                        id="companyNameTxt"
                        disabled={true}
                        label={intl.formatMessage({
                          defaultMessage: "Company Name",
                          description: "input label"
                        })}
                        value={assignedCompanyNameByBrandId}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className={classes.propGrid}>
                    <div className={classes.prop}>
                      <TextField
                        id="storeTxt"
                        disabled={true}
                        label={intl.formatMessage({
                          defaultMessage: "Store",
                          description: "input label"
                        })}
                        value={assignedTenantName}
                        fullWidth
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : null}
            <div>
              <div className={classes.propGrid}>
                <div className={classes.prop}>
                  <TextField
                    id="dateCreatedTxt"
                    disabled={true}
                    label={intl.formatMessage({
                      defaultMessage: "Date Created",
                      description: "input label"
                    })}
                    value={
                      !!staffMember?.dateJoined
                        ? Moment(staffMember.dateJoined).format(
                            "MMMM D YYYY, h:mm:ss a"
                          )
                        : ""
                    }
                    fullWidth
                  />
                </div>
              </div>
            </div>
            {canChangePassword && (
              <div className={classes.prop} id="updatePasswordBtn">
                <Button onClick={onChangePassword}>
                  <FormattedMessage
                    defaultMessage="Update Password"
                    description="button"
                  />
                </Button>
              </div>
            )}
            {canChangeOtherStaffPassword && (
              <div className={classes.prop} id="updatePasswordBtn">
                <Button onClick={onChangeOtherPw}>
                  <FormattedMessage
                    defaultMessage="Update Password"
                    description="button"
                  />
                </Button>
              </div>
            )}
          </CardContent>

          {!!formErrors.id && (
            <CardContent>
              <Typography color="error">
                {getStaffErrorMessage(formErrors.id, intl)}
              </Typography>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};
StaffProperties.displayName = "StaffProperties";
export default StaffProperties;
